import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { forkJoin, Observable, Subject } from 'rxjs';
import { NgbActiveModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ActionsSubject, Store } from '@ngrx/store';
import { TreatmentType } from 'it/primatoo/openapi/api/org/schemas/treatmentdoc/TreatmentType';
import { TreatmentService } from '@pt/api-client';
import { ObsoleteDoc } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listobsoletedocresponse/ObsoleteDoc';
import { ColDef, GridOptions } from 'ag-grid-community';
import { AgGridTableWrapper } from '../../utils/ag-grid-table-wrapper.service';
import { ColumnType } from 'it/primatoo/openapi/api/org/schemas/common/ColumnType';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DocumentItemUtils, PRIMATOO_BASE_PROD_ENVIRONMENT, PtCoreSelectors } from '@pt/pt-core';
import {
  AG_GRID_LOCALE_EN,
  IoniconsFilled,
  PtEntity,
  UiDocumentItem,
} from '@pt/pt-models';
import { ag_grid_frameworkComponents } from '../../pt-shared.constant';
import { CustomRendererComponent } from '../../ag-grid/renderers/custom-renderer.component';
import { AgGridModule } from 'ag-grid-angular';
import { NgIf, NgStyle } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { DocAccess } from 'it/primatoo/openapi/api/org/schemas/common/DocAccess';
import { Validated } from 'it/primatoo/openapi/api/org/schemas/common/docs/_findallvalidateddocresponse/Validated';
import { ObsoleteDetailsComponent } from './obsolete-details.component';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { LegalEntity } from '@ms-organization-entities';

@Component({
  standalone: true,
  selector: 'pt-obsolete-document-dialog',
  templateUrl: './obsolete-document-dialog.component.html',
  imports: [
    TranslateModule,
    AgGridModule,
    NgIf,
    IonicModule,
    NgStyle,
    ObsoleteDetailsComponent,
    NgbTooltip,
  ],
})
export class ObsoleteDocumentDialogComponent implements OnInit, OnDestroy {
  @Input() treatmentType: TreatmentType = TreatmentType.HOLDER;
  @Input() entity: PtEntity = 'treatmentsHolder';
  @Input() deleteDocumentFn: (
    entity: PtEntity,
    docs: UiDocumentItem[]
  ) => Observable<boolean>;

  @ViewChild('actionCol', { static: true }) actionCol: TemplateRef<unknown>;
  @ViewChild('obsoleteCol', { static: true }) obsoleteCol: TemplateRef<unknown>;
  @ViewChild('noteCol', { static: true }) noteCol: TemplateRef<unknown>;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  public gridOptions!: GridOptions;
  public loading = false;
  public tooltipShowDelay = 0;
  public tooltipHideDelay = 2000;
  public obsoleteDocs: Array<ObsoleteDoc> = [];
  public obsoleteColumns: Array<ColDef> = [];

  public headerName: string = 'Name';
  public headerDocId: string = '#';
  public internalId: string = 'Internal Id';

  public headerLegalEntity: string = 'Legal Entity';
  public headerDescription: string = 'Note';

  public headerObsoleteAt: string = 'Date';

  public IconDelete = IoniconsFilled.trash;
  public detailCellRenderer: any = CustomRendererComponent;
  public validated: Array<Validated> = [];

  isRowMaster = (dataItem) => {
    return true;
  };

  constructor(
    public store: Store,
    public activeModal: NgbActiveModal,
    public actionsSubject: ActionsSubject,
    public treatmentService: TreatmentService,
    private translateService: TranslateService
  ) {
    this.translateService.stream('AG_GRID.HEADERS.name').subscribe((v) => {
      this.headerName = v;
    });

    this.translateService
      .stream('AG_GRID.HEADERS.legalEntity')
      .subscribe((v) => {
        this.headerLegalEntity = v;
      });
    this.translateService.stream('AG_GRID.HEADERS.note').subscribe((v) => {
      this.headerDescription = v;
    });

    this.translateService
      .stream('AG_GRID.HEADERS.obsoleteAt')
      .subscribe((v) => {
        this.headerObsoleteAt = v;
      });
  }

  private loadColDef() {
    this.obsoleteColumns = [
      {
        ...AgGridTableWrapper.generateColDef({
          label: this.internalId,
          column: 'internalId',
          uiAvailable: true,
          uiUserShow: true,
          type: ColumnType.TEXT,
          operations: [],
        }),
        cellRenderer: 'agGroupCellRenderer',
      },
      AgGridTableWrapper.generateColDef({
        label: this.headerName,
        column: 'name',
        uiAvailable: true,
        uiUserShow: true,
        type: ColumnType.TEXT,
        operations: [],
      }),
      AgGridTableWrapper.generateColDef({
        label: this.headerLegalEntity,
        column: 'legalEntityName',
        uiAvailable: true,
        uiUserShow: true,
        type: ColumnType.TEXT,
        operations: [],
      }),
      AgGridTableWrapper.generateColDef({
        label: 'Legal Entity Id',
        column: 'legalEntityId',
        uiAvailable: false,
        uiUserShow: false,
        type: ColumnType.NUMBER,
        operations: [],
      }),
      {
        ...AgGridTableWrapper.generateColDef({
          label: this.headerDescription,
          column: 'description',
          uiAvailable: true,
          uiUserShow: true,
          type: ColumnType.TEXT,
          operations: [],
        }),
        cellRenderer: CustomRendererComponent,
        cellRendererParams: {
          ngTemplate: this.noteCol,
        },
      },
      {
        headerName: '',
        field: 'agGridActionsCol',
        flex: 1,
        width: 70,
        minWidth: 70,
        pinned: 'right',
        suppressNavigable: true,
        lockVisible: true,
        resizable: false,
        editable: false,
        suppressMovable: true,
        cellClass: 'no-border',
        cellRenderer: CustomRendererComponent,
        cellRendererParams: {
          ngTemplate: this.actionCol,
        },
      },
    ];
  }

  ngOnInit(): void {
    this.loading = true;
    this.loadColDef();

    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  // aggiorna deleteRow
  deleteRow(row: ObsoleteDoc) {
    this.deleteDocumentFn(this.entity, [
      DocumentItemUtils.createDocumentItem(
        row.docId,
        row.name,
        undefined,
        undefined,
        DocAccess.FULL,
        undefined,
        this.treatmentType
      ),
    ]).subscribe(() => this.loadData());
  }

  private loadDataFilterLegalEntities(le: LegalEntity[]) {
    const leIds = le.length > 0 ? le.map(l => l.id) : [];
    this.treatmentService
      .getObsolete(this.treatmentType, leIds)
      .subscribe(
        (s) => {
          this.obsoleteDocs = s.docs;
          this.gridOptions = {
            ...PRIMATOO_BASE_PROD_ENVIRONMENT.defaultGridOptions,
            rowData: this.obsoleteDocs,
            columnDefs: this.obsoleteColumns,
            localeText: AG_GRID_LOCALE_EN,
            suppressPropertyNamesCheck: true,
            components: ag_grid_frameworkComponents,
            rowBuffer: 5,
            pinnedBottomRowData: [
              this.obsoleteDocs ? this.obsoleteDocs?.length : 0,
            ],
            fullWidthCellRenderer: 'footerRenderComponent',
            detailCellRenderer: CustomRendererComponent,
            detailCellRendererParams: {
              ngTemplate: this.obsoleteCol,
            },
            detailRowHeight: 150,
            // detailRowAutoHeight: true,
            isFullWidthRow: (params) => params.rowNode.rowPinned === 'bottom',
            getContextMenuItems: () =>
              PRIMATOO_BASE_PROD_ENVIRONMENT.defaultContextMenuItems,
          } as GridOptions;
          this.loading = false;
        },
        () => {
          this.obsoleteDocs = [];
          this.loading = false;
        }
      );
  }
  private loadData() {
    this.gridOptions = undefined;
    this.loading = true;
    this.store.select(PtCoreSelectors.selectedLegalEntity).subscribe((v) => {
      if (v) {
        this.loadDataFilterLegalEntities(v || [])
      }
    })
  }
}
